/**
 * 체험단 캠페인 상세/신청 페이지 (v2 - 레뷰 스타일)
 *
 * 레이아웃:
 * - 데스크탑: 왼쪽 콘텐츠(border-b 섹션) + 오른쪽 sticky 네비게이션
 * - 모바일: 가로선 구분 + 하단 sticky 신청하기 버튼
 *
 * 주요 특징:
 * - 카드뷰 → 가로선(border-b)으로 섹션 구분
 * - 사이드바: 일정 요약 + 네비게이션 링크만
 * - IntersectionObserver로 현재 섹션 하이라이트
 * - 모바일 sticky "신청하기" 버튼
 */
import { initPixel, trackPixelEvent } from '@/lib/metaPixel'
import {
  PublicCampaignResponse,
  isLoggedIn,
  saveSessionToken,
  seedingPublicApi,
} from '@/lib/seedingPublicApi'
import { Linkify } from '@/components/Linkify'
import { sanitizeHtml } from '@/lib/sanitize'
import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useEffect, useRef, useState } from 'react'

// 하위 컴포넌트 (features/ 디렉토리로 이동됨)
import { Footer } from '@/features/apply/components/Footer'
import { ContactCard } from '@/features/apply/components/ContactCard'
import { DetailImageSection } from '@/features/apply/components/DetailImageSection'
import { SidebarSection, type SectionId } from '@/features/apply/components/SidebarSection'
import { MobileInfoSection } from '@/features/apply/components/MobileInfoSection'
import { MemberOnlySignupFlow } from '@/features/apply/components/MemberOnlySignupFlow'
import { ApplicationForm } from '@/features/apply/components/ApplicationForm'
import { formatShortDate } from '@/features/apply/components/utils'

export const Route = createFileRoute('/apply/$mallId/$shopNo/$campaignId')({
  component: CampaignDetailPage,
})

/** 컴포넌트 props (short URL 라우트에서도 재사용) */
export interface CampaignDetailContentProps {
  mallId: string
  shopNo: number
  campaignId: string
  submitPath?: string          // 미션 제출 페이지 경로 (short URL용)
  previewData?: PublicCampaignResponse  // 미리보기 모드: API 호출 대신 직접 데이터 전달
  previewMode?: boolean                  // 미리보기 모드: 인터랙션 비활성화
  forceMobile?: boolean                  // 강제 모바일 레이아웃 (미리보기 모바일 뷰)
}

/** 캠페인 상세/신청 컴포넌트 (라우트 비종속) */
export function CampaignDetailContent({ mallId, shopNo: shopNoInt, campaignId, submitPath, previewData, previewMode, forceMobile }: CampaignDetailContentProps) {

  const [campaign, setCampaign] = useState<PublicCampaignResponse | null>(previewData || null)
  const [isLoading, setIsLoading] = useState(!previewData)
  const [error, setError] = useState<string | null>(null)
  const [loggedIn, setLoggedIn] = useState(false)
  const [applySuccess, setApplySuccess] = useState(false)

  // 각 섹션의 ref (IntersectionObserver + 네비게이션용)
  const sectionRefs = {
    product: useRef<HTMLDivElement>(null),
    detail: useRef<HTMLDivElement>(null),
    guide: useRef<HTMLDivElement>(null),
    apply: useRef<HTMLDivElement>(null),
  }
  const [activeSection, setActiveSection] = useState<SectionId>('product')

  // 모바일 sticky 신청하기 버튼 표시 여부
  const [showStickyApply, setShowStickyApply] = useState(true)

  // ==================== 초기 로드 (OAuth, 캠페인, Pixel) ====================

  useEffect(() => {
    // 미리보기 모드: API/OAuth/Pixel 모두 스킵
    if (previewMode) return

    // URL hash에서 OAuth 데이터 확인 (리다이렉트 모드 — 팝업 차단 시 사용)
    const hash = window.location.hash
    if (hash.startsWith('#oauth=')) {
      try {
        const hashData = JSON.parse(decodeURIComponent(hash.slice('#oauth='.length)))
        if (hashData.session_token) {
          saveSessionToken(hashData.session_token)
          setLoggedIn(true)
          if (hashData.prefill) {
            localStorage.setItem('seeding_prefill', JSON.stringify(hashData.prefill))
          }
        }
      } catch (e) {
        console.error('OAuth hash parse error:', e)
      }
      // hash 정리 (토큰이 URL에 남지 않도록)
      window.history.replaceState(null, '', window.location.pathname + window.location.search)
    }

    // 로그인 상태 체크 (토큰 유효성 확인)
    const checkLoginStatus = async () => {
      if (isLoggedIn()) {
        try {
          await seedingPublicApi.getMe()
          setLoggedIn(true)
        } catch {
          setLoggedIn(false)
        }
      } else {
        setLoggedIn(false)
      }
    }
    checkLoginStatus()

    // OAuth 팝업에서 로그인 완료 감지 (postMessage)
    const handleOAuthMessage = (e: MessageEvent) => {
      if (e.data?.type === 'oauth_callback_success' && e.data.session_token) {
        saveSessionToken(e.data.session_token)
        setLoggedIn(true)
        if (e.data.prefill) {
          localStorage.setItem('seeding_prefill', JSON.stringify(e.data.prefill))
        }
      }
    }
    window.addEventListener('message', handleOAuthMessage)

    // 캠페인 로드
    const fetchCampaign = async () => {
      setIsLoading(true)
      try {
        const data = await seedingPublicApi.getCampaign(mallId, shopNoInt, campaignId)
        setCampaign(data)

        // 브라우저 탭 타이틀 설정
        document.title = `${data.title} | 체험톡톡`

        // Meta Pixel 초기화 + PageView/ViewContent 이벤트
        if (data.meta_pixel_id) {
          initPixel(data.meta_pixel_id)
          trackPixelEvent(data.meta_pixel_id, 'PageView', {
            page_url: window.location.href,
            page_title: document.title,
          })
        }
      } catch (err: any) {
        setError(err.message || '캠페인을 불러오는데 실패했어요.')
      } finally {
        setIsLoading(false)
      }
    }
    fetchCampaign()

    return () => window.removeEventListener('message', handleOAuthMessage)
  }, [mallId, shopNoInt, campaignId, previewMode])

  // ==================== IntersectionObserver ====================

  useEffect(() => {
    if (!campaign) return

    const observers: IntersectionObserver[] = []
    const entries = Object.entries(sectionRefs) as [SectionId, React.RefObject<HTMLDivElement>][]

    entries.forEach(([id, ref]) => {
      if (!ref.current) return
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveSection(id)
        },
        { rootMargin: '-20% 0px -60% 0px' },
      )
      observer.observe(ref.current)
      observers.push(observer)
    })

    return () => observers.forEach((o) => o.disconnect())
  }, [campaign])

  // 모바일 sticky 버튼: 신청 폼이 뷰포트에 보이면 숨김
  useEffect(() => {
    if (campaign?.status !== 'recruiting') return
    const applyRef = sectionRefs.apply.current
    if (!applyRef) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowStickyApply(!entry.isIntersecting)
      },
      { threshold: 0.1 },
    )
    observer.observe(applyRef)
    return () => observer.disconnect()
  }, [campaign])

  // ==================== 네비게이션 ====================

  const handleNavigate = useCallback((sectionId: SectionId) => {
    sectionRefs[sectionId]?.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  const scrollToForm = useCallback(() => {
    sectionRefs.apply.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  // ==================== Cafe24 OAuth 로그인 ====================

  const handleLogin = async () => {
    const popup = window.open('about:blank', '_blank')
    try {
      const { auth_url } = await seedingPublicApi.getAuthUrl(mallId, shopNoInt, window.location.href)
      if (popup && !popup.closed) {
        popup.location.href = auth_url
      } else {
        window.location.href = auth_url
      }
    } catch (err: any) {
      if (popup && !popup.closed) popup.close()
      setError(err.message || '로그인 처리 중 오류가 발생했어요.')
    }
  }

  // ==================== 렌더링: 로딩/에러/성공 상태 ====================

  if (isLoading) {
    return (
      <div className="min-h-screen bg-surface-lowest">
        <div className="max-w-5xl mx-auto px-4 py-8">
          <div className="animate-pulse">
            <div className="h-64 bg-surface-high rounded-lg mb-6"></div>
            <div className="h-8 bg-surface-high rounded w-3/4 mb-4"></div>
            <div className="h-4 bg-surface-high rounded w-1/2"></div>
          </div>
        </div>
      </div>
    )
  }

  if (!campaign) {
    return (
      <div className="min-h-screen bg-surface-lowest flex items-center justify-center">
        <div className="text-center">
          <p className="text-on-surface-variant">{error || '캠페인을 찾을 수 없어요.'}</p>
        </div>
      </div>
    )
  }

  if (applySuccess) {
    return (
      <div className="min-h-screen bg-surface-lowest flex items-center justify-center">
        <div className="max-w-md mx-auto px-4">
          <div className="bg-surface-lowest rounded-lg shadow-lg p-8 text-center">
            <h1 className="text-xl font-bold text-on-surface mb-2">신청 완료!</h1>
            <p className="text-on-surface-variant mb-6">
              체험단 신청이 접수되었어요.
              <br />
              선정 결과는 알림톡으로 안내드립니다.
            </p>
          </div>
        </div>
      </div>
    )
  }

  // ==================== 렌더링: 메인 페이지 ====================

  const canApply = !campaign.login_required || loggedIn
  const fromAlimtalk = new URLSearchParams(window.location.search).get('from') === 'alimtalk'
  const hasDetailImages = campaign.detail_images && campaign.detail_images.length > 0

  return (
    <div className="min-h-screen bg-surface-lowest flex flex-col">
      <main className="max-w-5xl mx-auto px-4 py-8 flex-1 w-full">
        {/* 제목 + 배지 */}
        <div className="pb-6 border-b border-border">
          <div className="flex items-center gap-2 mb-3">
            {(() => {
              const badgeConfig: Record<string, { bg: string; text: string; label: string }> = {
                scheduled: { bg: 'bg-purple-100', text: 'text-purple-800', label: '모집예정' },
                recruiting: { bg: 'bg-green-100', text: 'text-green-800', label: '모집중' },
                pending_selection: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: '선정 대기' },
                in_progress: { bg: 'bg-primary/10', text: 'text-primary', label: '진행중' },
                completed: { bg: 'bg-surface-low', text: 'text-on-surface-variant', label: '종료' },
              }
              const config = badgeConfig[campaign.status] || { bg: 'bg-surface-low', text: 'text-on-surface', label: '모집마감' }
              return (
                <span className={`px-3 py-1 ${config.bg} ${config.text} rounded-full text-sm font-medium`}>
                  {config.label}
                </span>
              )
            })()}
          </div>
          <h1 className="text-xl font-bold text-on-surface flex items-center gap-2 flex-wrap">
            {campaign.title}
            {campaign.shop_name && (
              <span className="text-sm font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                {campaign.shop_name}
              </span>
            )}
          </h1>
        </div>

        {/* 2칼럼 그리드 (데스크탑) / 단일 칼럼 (모바일) */}
        <div className={forceMobile ? '' : 'lg:grid lg:grid-cols-[5fr_2fr] lg:gap-8'}>
          {/* ========== 왼쪽 콘텐츠 영역 ========== */}
          <div>
            {/* 모바일: 일정 정보 (인라인) + 작은 썸네일 */}
            <div className={`${forceMobile ? '' : 'lg:hidden'} py-6 border-b border-border`}>
              <MobileInfoSection campaign={campaign} />
            </div>

            {/* 썸네일 이미지 */}
            {campaign.thumbnail_url && (
              <div className="py-6 border-b border-border">
                <div className="rounded-lg overflow-hidden">
                  <img src={campaign.thumbnail_url} alt={campaign.title} className="w-full object-cover" />
                </div>
              </div>
            )}

            {/* 제공 내역 */}
            <div ref={sectionRefs.product} className="py-8 border-b border-border">
              <h2 className="text-lg font-semibold text-on-surface mb-3">제공 내역</h2>
              <div className="flex gap-4">
                {campaign.product_image_url && (
                  <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                    <img src={campaign.product_image_url} alt={campaign.product_name || campaign.title} className="w-full h-full object-cover" />
                  </div>
                )}
                <div>
                  <p className="text-sm font-medium text-on-surface">{campaign.product_name}</p>
                  {campaign.product_price && (
                    <p className="text-sm text-on-surface-variant mt-1">
                      {(() => {
                        const variants = campaign.allowed_variants
                        if (variants && variants.length > 1) {
                          const prices = variants.map(v => v.price)
                          const minP = Math.min(...prices)
                          const maxP = Math.max(...prices)
                          if (minP !== maxP) return `최대 ${maxP.toLocaleString()}원 상당`
                        }
                        return `${campaign.product_price.toLocaleString()}원 상당`
                      })()}
                    </p>
                  )}
                  {campaign.product_url && (
                    <a href={campaign.product_url} target="_blank" rel="noopener noreferrer" className="inline-block mt-2 text-base font-bold text-primary hover:text-primary transition-colors">
                      상품 보러가기 &rarr;
                    </a>
                  )}
                </div>
              </div>
              {/* 구매형 안내 */}
              {(!campaign.delivery_method || campaign.delivery_method === 'cafe24_order') && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm font-medium text-amber-800">
                    이 체험단은 선정 후 직접 주문하는 방식이에요
                  </p>
                  <p className="text-xs text-amber-700 mt-1">
                    {campaign.auto_coupon
                      ? '선정 시 전액 할인 쿠폰이 발급되며, 쿠폰을 적용하여 직접 상품을 주문해주세요.'
                      : '선정 후 안내에 따라 직접 상품을 주문해주세요.'}
                  </p>
                </div>
              )}
            </div>

            {/* 캠페인 소개 */}
            <div ref={sectionRefs.detail} className="py-8 border-b border-border">
              <h2 className="text-lg font-semibold text-on-surface mb-3">캠페인 소개</h2>
              {hasDetailImages && (
                <DetailImageSection images={campaign.detail_images!} />
              )}
              {campaign.description && (
                <div className="prose prose-sm max-w-none whitespace-pre-line">
                  <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(campaign.description) }} />
                </div>
              )}
            </div>

            {/* 미션 안내 (차수별 가이드라인) */}
            {campaign.mission_rounds && campaign.mission_rounds.length > 0 && (
              <div ref={sectionRefs.guide} className="py-8 border-b border-border">
                <h2 className="text-lg font-semibold text-on-surface mb-3">미션 안내</h2>
                {campaign.mission_rounds.length > 1 ? (
                  <div className="divide-y divide-border">
                    {campaign.mission_rounds.map((round) => (
                      <div key={round.round} className="py-4 first:pt-0">
                        <h3 className="text-base font-semibold text-on-surface">
                          {round.round}차{round.title ? ` — ${round.title}` : ''}
                        </h3>
                        <p className="text-xs text-outline-variant mt-0.5">
                          마감 {formatShortDate(round.deadline_at)}
                        </p>
                        {round.guideline && (
                          <p className="text-sm text-on-surface-variant whitespace-pre-line mt-2">
                            <Linkify>{round.guideline}</Linkify>
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : campaign.mission_rounds[0]?.guideline ? (
                  <div>
                    <div className="text-sm text-on-surface prose prose-sm max-w-none whitespace-pre-line">
                      <Linkify>{campaign.mission_rounds[0].guideline!}</Linkify>
                    </div>
                  </div>
                ) : null}
              </div>
            )}

            {/* 신청 영역 */}
            {campaign.status === 'recruiting' ? (
              <div ref={sectionRefs.apply} className="py-8">
                <h2 className="text-lg font-semibold text-on-surface mb-6">체험단 신청</h2>

                {previewMode ? (
                  <ApplicationForm
                    campaign={campaign}
                    mallId={mallId}
                    shopNo={shopNoInt}
                    campaignId={campaignId}
                    onSuccess={() => {}}
                    previewMode
                    forceMobile={forceMobile}
                  />
                ) : (
                  <>
                    {campaign.login_required &&
                      !loggedIn &&
                      (fromAlimtalk ? (
                        <div className="mb-6">
                          <div className="p-4 bg-primary/10 border border-primary/30 rounded-lg mb-4">
                            <p className="text-primary font-medium mb-1">
                              이 캠페인은 {campaign.shop_name || '쇼핑몰'} 회원만 신청할 수 있습니다.
                            </p>
                            <p className="text-primary text-sm">
                              회원가입을 완료하셨다면 로그인 후 신청해주세요.
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={handleLogin}
                            className="w-full py-3 bg-primary text-white rounded-lg hover:bg-primary-dim font-medium"
                          >
                            로그인하고 신청하기
                          </button>
                        </div>
                      ) : (
                        <MemberOnlySignupFlow
                          campaign={campaign}
                          mallId={mallId}
                          shopNo={shopNoInt}
                          campaignId={campaignId}
                          onLogin={handleLogin}
                        />
                      ))}

                    {canApply && (
                      <ApplicationForm
                        campaign={campaign}
                        mallId={mallId}
                        shopNo={shopNoInt}
                        campaignId={campaignId}
                        onSuccess={() => setApplySuccess(true)}
                      />
                    )}
                  </>
                )}
              </div>
            ) : campaign.status === 'scheduled' ? (
              <div className="py-8">
                <div className="bg-surface-low rounded-lg p-8 text-center">
                  <p className="text-on-surface-variant">모집 예정입니다.</p>
                </div>
              </div>
            ) : (
              <div className="py-8">
                <div className="bg-surface-low rounded-lg p-8 text-center">
                  <p className="text-on-surface-variant">모집이 마감되었어요.</p>
                </div>
              </div>
            )}

            {/* 운영자(고객사) 문의처 — 신청 폼 하단에 눈에 띄게 노출 (미등록이면 자동 숨김) */}
            <ContactCard contact={campaign?.contact} shopName={campaign?.shop_name} className="mb-2" />
          </div>

          {/* ========== 오른쪽: 사이드바 ========== */}
          <div className={forceMobile ? 'hidden' : 'hidden lg:block'}>
            <SidebarSection
              campaign={campaign}
              activeSection={activeSection}
              onNavigate={handleNavigate}
            />
          </div>
        </div>
      </main>

      {/* 모바일 전용 플로팅 신청하기 pill */}
      {!previewMode && campaign.status === 'recruiting' && showStickyApply && (
        <button
          type="button"
          onClick={scrollToForm}
          className="lg:hidden fixed bottom-6 right-6 z-50 px-5 py-2.5 bg-primary text-white text-sm font-semibold rounded-full shadow-lg hover:bg-primary-dim active:bg-primary-dim transition-colors"
        >
          신청하기 &darr;
        </button>
      )}

      <Footer />
    </div>
  )
}

/** 기존 라우트 wrapper (Route.useParams → props 전달) */
function CampaignDetailPage() {
  const { mallId, shopNo, campaignId } = Route.useParams()
  return <CampaignDetailContent mallId={mallId} shopNo={parseInt(shopNo, 10)} campaignId={campaignId} />
}
